import React, { useRef, useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Printer } from 'lucide-react';
import { toast } from 'react-toastify';

const PaymentReceiptModal = ({ isOpen, onClose, receiptData }) => {
  const printRef = useRef();
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [logoError, setLogoError] = useState(false);
  
  const handlePrint = () => {
    const printWindow = window.open('', 'PRINT');
    const content = printRef.current.innerHTML;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Reçu de paiement</title>
          <style>
            @page { size: A4; margin: 8mm; }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: Arial, sans-serif; 
              font-size: 10pt; 
              line-height: 1.2; 
              color: #000; 
            }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #000; padding: 3px 5px; vertical-align: top; }
            th { background-color: #f0f0f0; font-weight: bold; }
            .no-border { border: none; }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .bold { font-weight: bold; }
      @media print {
              body { margin: 0; }
            }
          </style>
        </head>
        <body>
          ${content}
          <script>
            window.onload = function() {
              setTimeout(() => { window.print(); window.close(); }, 100);
            }
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };



  if (!isOpen || !receiptData) return null;

  // Fonctions utilitaires
  const formatDate = (dateString) => {
    if (!dateString) return new Date().toLocaleDateString('fr-FR');
    try {
      return new Date(dateString).toLocaleDateString('fr-FR');
    } catch {
      return new Date().toLocaleDateString('fr-FR');
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const formatCurrency = (amount) => {
    return (amount || 0).toLocaleString('fr-FR');
  };

  // Extraction des données
  const etablissement = receiptData.etablissement || {
    libelle: 'ÉCOLE SUPÉRIEURE DE TECHNOLOGIE (E.S.T)',
    devise: 'Travail-Progrès-Réussite',
    adresse: 'Niamey/Rue des Ecoles',
    bp: '8000',
    pays: 'Niger',
    contact: '99956432',
    email: 'est@gmail.com',
    site_web: 'www.est.edu.ne',
    ministere: 'Ministère de l\'Enseignement Supérieur, de la Recherche et de l\'Innovation',
    logo: receiptData.etablissement?.logo_url || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjRjVGNUY1IiBzdHJva2U9IiMzMzMiLz4KPHRleHQgeD0iNDAiIHk9IjMwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZvbnQtd2VpZ2h0PSJib2xkIiBmaWxsPSIjMzMzIj5FLlMuVDwvdGV4dD4KPHRleHQgeD0iNDAiIHk9IjUwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iOCIgZmlsbD0iIzY2NiI+TE9HTzwvdGV4dD4KPC9zdmc+'
  };





  const etudiant = receiptData.etudiant || {
    prenom: receiptData.student_first_name || '',
    nom: receiptData.student_last_name || '',
    matricule: receiptData.student_matricule || receiptData.matricule || '',
    classe: receiptData.student_class || receiptData.classe || ''
  };

  const modePaiement = receiptData.mode_paiement || { libelle: 'Espèces' };
  const dateVersement = receiptData.date_versement || receiptData.created_at || new Date().toISOString();
  const commentaire = receiptData.commentaire || receiptData.note || '';

  const montantVerse = receiptData.montant_verse || receiptData.montant || 0;
  const montantTotal = receiptData.montant_total || 0;
  const montantPaye = receiptData.montant_paye_total || receiptData.montant_paye || 0;
  const montantRestant = receiptData.montant_restant || receiptData.montant_impaye || 0;

  const lignesVersement = receiptData.lignes_versement || [];
  if (lignesVersement.length === 0 && montantVerse > 0) {
    lignesVersement.push({
      libelle: 'Frais de scolarité',
      montant: montantVerse
    });
  }

  const numeroRecu = receiptData.receipt_number || receiptData.numero_recu || `REC-${Date.now()}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] overflow-hidden">
        
        {/* Header Controls */}
        <div className="flex justify-between items-center p-4 border-b bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-800">Reçu de Paiement</h2>
          <div className="flex space-x-2">
            <button
              onClick={handlePrint}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              <Printer className="h-4 w-4 mr-2" />
              Imprimer
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Receipt Content */}
        <div className="overflow-y-auto max-h-[calc(95vh-80px)] p-4 bg-gray-50">
                     <div 
             ref={printRef} 
             className="bg-white max-w-[210mm] mx-auto shadow-sm"
             style={{
               fontFamily: 'Arial, sans-serif',
               fontSize: '10pt',
               lineHeight: '1.2',
               color: '#000',
               padding: '8mm',
               position: 'relative'
             }}
           >
                         {/* En-tête officiel avec logo */}
             <div style={{ marginBottom: '1mm' }}>
                              {/* Logo de l'établissement - Position absolue */}
               <div style={{ 
                 position: 'absolute',
                 top: '1mm',
                 left: '5mm',
                 width: '22mm',
                 height: '22mm',
                 padding: '1mm',
                 backgroundColor: '#fff',
                 zIndex: 1
               }}>
                      {etablissement.logo ? (
                        <img 
                          src={etablissement.logo} 
                          alt="Logo établissement" 
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain'
                            }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        ) : (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '6pt',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            color: '#666'
                          }}>
                            LOGO<br/>{etablissement.libelle.split(' ')[0]}
                        </div>
                        )}
                 {etablissement.logo && (
                   <div style={{
                     display: 'none',
                     width: '100%',
                     height: '100%',
                     alignItems: 'center',
                     justifyContent: 'center',
                     fontSize: '6pt',
                     fontWeight: 'bold',
                     textAlign: 'center',
                     color: '#666'
                   }}>
                     LOGO<br/>{etablissement.libelle.split(' ')[0]}
                      </div>
                 )}
                    </div>

               {/* En-tête République et Établissement - Centré avec marge pour éviter le logo */}
               <div style={{ textAlign: 'center', marginBottom: '2mm', marginTop: '0mm', marginLeft: '25mm', marginRight: '12mm' }}>
                 <h1 style={{ 
                   fontSize: '16pt', 
                   fontWeight: 'bold', 
                   margin: '0 0 2mm 0', 
                   letterSpacing: '2pt',
                   textTransform: 'uppercase'
                 }}>
                   RÉPUBLIQUE DU {etablissement.pays.toUpperCase()}
                 </h1>
                 <p style={{ 
                   fontSize: '12pt', 
                   margin: '0 0 2mm 0', 
                   fontWeight: '600',
                   lineHeight: '1.3'
                 }}>
                   Ministère {etablissement.ministere}
                 </p>
                 
                 <div style={{ 
                   width: '80mm', 
                   height: '2pt', 
                   backgroundColor: '#000', 
                   margin: '2mm auto' 
                 }}></div>

                 <h2 style={{ 
                   fontSize: '16pt', 
                   fontWeight: 'bold', 
                   margin: '2mm 0 2mm 0',
                   textTransform: 'uppercase',
                   letterSpacing: '1pt'
                 }}>
                        {etablissement.libelle}
                 </h2>
                 
                 <p style={{ 
                   fontSize: '12pt', 
                   fontStyle: 'italic', 
                   margin: '0 0 2mm 0',
                   fontWeight: '500'
                 }}>
                        « {etablissement.devise} »
                      </p>
                      
                                  {/* Informations de contact - Organisées linéairement */}
                 <div style={{ 
                   fontSize: '11pt', 
                   lineHeight: '1.4',
                   margin: '0 auto',
                   maxWidth: '160mm'
                 }}>
                   <p style={{ margin: '1mm 0' }}>
                     <strong>Adresse Physique:</strong> {etablissement.adresse}
                   </p>
                   <p style={{ margin: '1mm 0' }}>
                     <strong>Boîte Postale:</strong> BP {etablissement.bp}, {etablissement.pays}
                   </p>
                   <p style={{ margin: '1mm 0' }}>
                     <strong>Téléphone:</strong> {etablissement.contact} | 
                     <strong> Email:</strong> {etablissement.email}
                   </p>
                          {etablissement.site_web && (
                     <p style={{ margin: '1mm 0' }}>
                       <strong>Site Web:</strong> {etablissement.site_web}
                     </p>
                          )}
                        </div>
                      </div>

              </div>
                  
            {/* Titre du document avec numéro */}
            <div style={{ textAlign: 'center', margin: '2mm 0' }}>
              <div style={{ 
                display: 'inline-block', 
                border: '2pt solid #000', 
                padding: '4mm 12mm', 
                backgroundColor: '#f5f5f5'
              }}>
                <h3 style={{ 
                  fontSize: '16pt', 
                  fontWeight: 'bold', 
                  margin: '0', 
                  letterSpacing: '1pt',
                  color: '#000'
                }}>
                  REÇU DE PAIEMENT N° {numeroRecu}
                </h3>
                <p style={{ 
                  fontSize: '12pt', 
                  margin: '2mm 0 0 0', 
                  color: '#666'
                }}>
                  Date: {formatDate(dateVersement)}
                </p>
              </div>
            </div>

            {/* Informations en deux colonnes */}
            <table style={{ width: '100%', marginBottom: '4mm' }}>
              <tbody>
                <tr>
                  <td style={{ border: 'none', width: '50%', paddingRight: '3mm' }}>
                    <div style={{ border: '1pt solid #000', padding: '4mm', backgroundColor: '#fafafa' }}>
                      <h4 style={{ fontSize: '11pt', fontWeight: 'bold', margin: '0 0 3mm 0', textTransform: 'uppercase' }}>
                        Informations Étudiant
                      </h4>
                      <table style={{ width: '100%', fontSize: '10pt' }}>
                        <tbody>
                          <tr>
                            <td style={{ border: 'none', fontWeight: 'bold', width: '35%', padding: '0.5mm 0' }}>
                              Nom:
                            </td>
                            <td style={{ border: 'none', padding: '0.5mm 0' }}>
                              {etudiant.prenom} {etudiant.nom}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ border: 'none', fontWeight: 'bold', padding: '0.5mm 0' }}>
                              Matricule:
                            </td>
                            <td style={{ border: 'none', padding: '0.5mm 0' }}>
                              {etudiant.matricule}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ border: 'none', fontWeight: 'bold', padding: '0.5mm 0' }}>
                              Formation:
                            </td>
                            <td style={{ border: 'none', padding: '0.5mm 0' }}>
                              {etudiant.classe}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </td>
                  <td style={{ border: 'none', width: '50%', paddingLeft: '3mm' }}>
                    <div style={{ border: '1pt solid #000', padding: '3mm', backgroundColor: '#fafafa' }}>
                      <h4 style={{ fontSize: '9pt', fontWeight: 'bold', margin: '0 0 2mm 0', textTransform: 'uppercase' }}>
                        Détails du Paiement
                      </h4>
                      <table style={{ width: '100%', fontSize: '8pt' }}>
                        <tbody>
                          <tr>
                            <td style={{ border: 'none', fontWeight: 'bold', width: '35%', padding: '0.5mm 0' }}>
                              Date:
                            </td>
                            <td style={{ border: 'none', padding: '0.5mm 0' }}>
                              {formatDate(dateVersement)} {formatTime(dateVersement) && `à ${formatTime(dateVersement)}`}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ border: 'none', fontWeight: 'bold', padding: '0.5mm 0' }}>
                              Mode:
                            </td>
                            <td style={{ border: 'none', padding: '0.5mm 0' }}>
                              {modePaiement.libelle}
                            </td>
                          </tr>
                          {commentaire && (
                            <tr>
                              <td style={{ border: 'none', fontWeight: 'bold', padding: '0.5mm 0' }}>
                                Objet:
                              </td>
                              <td style={{ border: 'none', padding: '0.5mm 0' }}>
                                {commentaire}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

              {/* Tableau des montants */}
            <table style={{ width: '100%', marginBottom: '4mm', border: '2pt solid #000' }}>
                  <thead>
                <tr>
                  <th style={{ 
                    padding: '3mm', 
                    backgroundColor: '#f0f0f0', 
                    fontSize: '9pt', 
                    textAlign: 'left' 
                  }}>
                    DESCRIPTION DU VERSEMENT
                      </th>
                  <th style={{ 
                    padding: '3mm', 
                    backgroundColor: '#f0f0f0', 
                    fontSize: '9pt', 
                    textAlign: 'right',
                    width: '25%'
                  }}>
                    MONTANT (FCFA)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {lignesVersement.map((ligne, index) => (
                  <tr key={index}>
                    <td style={{ padding: '2mm', fontSize: '9pt' }}>
                      {ligne.libelle}
                        </td>
                    <td style={{ padding: '2mm', textAlign: 'right', fontSize: '9pt', fontFamily: 'monospace' }}>
                      {formatCurrency(ligne.montant)}
                        </td>
                      </tr>
                    ))}
                <tr>
                  <td style={{ 
                    padding: '3mm', 
                    fontWeight: 'bold', 
                    backgroundColor: '#f8f8f8', 
                    fontSize: '10pt',
                    border: '2pt solid #000'
                  }}>
                    TOTAL VERSÉ CE JOUR
                  </td>
                  <td style={{ 
                    padding: '3mm', 
                    textAlign: 'right', 
                    fontWeight: 'bold', 
                    backgroundColor: '#f8f8f8', 
                    fontSize: '11pt',
                    fontFamily: 'monospace',
                    border: '2pt solid #000'
                  }}>
                    {formatCurrency(montantVerse)} FCFA
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Situation financière - compact */}
            <div style={{ 
              border: '1pt solid #000', 
              padding: '3mm', 
              marginBottom: '4mm', 
              backgroundColor: '#fafafa' 
            }}>
              <h4 style={{ fontSize: '9pt', fontWeight: 'bold', textAlign: 'center', marginBottom: '2mm' }}>
                SITUATION FINANCIÈRE
              </h4>
              <table style={{ width: '100%', fontSize: '8pt' }}>
                <tbody>
                  <tr>
                    <td style={{ border: 'none', textAlign: 'center', padding: '1mm', fontWeight: 'bold' }}>
                      Total Dû
                    </td>
                    <td style={{ border: 'none', textAlign: 'center', padding: '1mm', fontWeight: 'bold' }}>
                      Déjà Payé
                    </td>
                    <td style={{ border: 'none', textAlign: 'center', padding: '1mm', fontWeight: 'bold' }}>
                      Reste
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: 'none', textAlign: 'center', padding: '1mm', fontFamily: 'monospace' }}>
                      {formatCurrency(montantTotal)}
                    </td>
                    <td style={{ border: 'none', textAlign: 'center', padding: '1mm', fontFamily: 'monospace' }}>
                      {formatCurrency(montantPaye)}
                      </td>
                    <td style={{ border: 'none', textAlign: 'center', padding: '1mm', fontFamily: 'monospace' }}>
                      {formatCurrency(montantRestant)}
                      </td>
                    </tr>
                  </tbody>
                </table>
            </div>

            {/* Signatures et QR Code - avec plus d'espace */}
            <table style={{ width: '100%', marginTop: '10mm' }}>
              <tbody>
                <tr>
                  <td style={{ border: 'none', textAlign: 'center', width: '25%', verticalAlign: 'bottom' }}>
                    <div style={{ fontSize: '9pt', fontWeight: 'bold', marginBottom: '3mm' }}>
                      Vérification
                </div>
                    <div style={{ border: '1pt solid #000', padding: '2mm', display: 'inline-block' }}>
                      <QRCodeSVG 
                        value={`${numeroRecu}|${montantVerse}|${formatDate(dateVersement)}`} 
                        size={50}
                      />
                </div>
                  </td>
                  <td style={{ border: 'none', textAlign: 'center', width: '37.5%', verticalAlign: 'bottom' }}>
                    <div style={{ fontSize: '10pt', fontWeight: 'bold', marginBottom: '20mm' }}>
                      Le Caissier
                </div>
                    <div style={{ borderBottom: '2pt solid #000', marginBottom: '2mm', width: '80%', margin: '0 auto' }}></div>
                    <div style={{ fontSize: '9pt' }}>Signature et cachet</div>
                  </td>
                  <td style={{ border: 'none', textAlign: 'center', width: '37.5%', verticalAlign: 'bottom' }}>
                    <div style={{ fontSize: '10pt', fontWeight: 'bold', marginBottom: '20mm' }}>
                      Le Responsable Financier
              </div>
                    <div style={{ borderBottom: '2pt solid #000', marginBottom: '2mm', width: '80%', margin: '0 auto' }}></div>
                    <div style={{ fontSize: '9pt' }}>Signature et cachet</div>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Footer */}
            <div style={{ 
              marginTop: '8mm', 
              paddingTop: '3mm', 
              borderTop: '1pt solid #000', 
              textAlign: 'center', 
              fontSize: '8pt' 
            }}>
              <p style={{ fontWeight: 'bold', marginBottom: '1mm' }}>
                DOCUMENT OFFICIEL - À CONSERVER
              </p>
              <p style={{ fontStyle: 'italic' }}>
                {etablissement.libelle} - Émis le {formatDate(new Date().toISOString())} à {formatTime(new Date().toISOString())}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentReceiptModal;